import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, Search, X } from 'lucide-react';
import {
  CANONICAL_SUBSEGMENTS,
  MARKETS,
  MARKET_NAMES,
  SPECIALTY_LABEL,
  SPECIALTY_TOKENS,
  SUBSEGMENT_FILTER_OPTIONS,
} from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { useGlobalCurrency, useSetPanelEntity, type PanelId, type TabId } from '../store.js';
import { formatEurInCurrency } from '../../currency.js';
import { useOwnershipNetwork } from '../../api/ownership-network-hooks.js';
import type { OwnershipGroupNode, OwnershipMember } from '../../api/client.js';
import { useGroupKey, useGroupLink } from '../group-link.js';

export const OWNERSHIP_CLASS_LABEL: Record<string, string> = {
  pe: 'Private equity',
  strategic: 'Corporate',
  listed: 'Public company',
  family: 'Family office',
  infra: 'Infrastructure',
  sovereign: 'Government / sovereign',
  reit: 'REIT',
  cooperative: 'Cooperative',
  foundation: 'Non-profit',
  independent: 'Founder / management owned',
};
const OWNERSHIP_OPTIONS = Object.keys(OWNERSHIP_CLASS_LABEL);
type Grouping = 'country' | 'holdco' | 'subsegment';

const button: React.CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 3,
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  padding: '5px 8px',
  font: '500 var(--t-meta) var(--font-ui)',
  cursor: 'pointer',
};

export function OwnershipNetworkPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const ccy = useGlobalCurrency();
  const setEntity = useSetPanelEntity(tabId, panelId);
  const groupKey = useGroupKey(tabId, panelId);
  const setGroupFor = useGroupLink((s) => s.setFor);
  const [countries, setCountries] = useState<Set<string>>(() => new Set(['GB']));
  const [subs, setSubs] = useState<Set<string>>(() => new Set());
  const [ownership, setOwnership] = useState<Set<string>>(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Group-results dropdown (the middle stage of the vertical flow:
  // search → matching groups → the chosen group's entities). Opens
  // automatically while the user is typing a search.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [grouping, setGrouping] = useState<Grouping>('country');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const canonical = useMemo(
    () => [...subs].filter((s) => (CANONICAL_SUBSEGMENTS as readonly string[]).includes(s)),
    [subs],
  );
  const specialty = useMemo(() => [...subs].filter((s) => SPECIALTY_TOKENS.has(s)), [subs]);
  const query = useMemo(
    () => ({
      country: [...countries],
      subsegment: canonical.length ? canonical : undefined,
      specialty: specialty.length ? specialty : undefined,
      ownership: ownership.size ? [...ownership] : undefined,
      minOpcos: 2,
      memberCap: 100,
    }),
    [countries, canonical, specialty, ownership],
  );
  const { data, isLoading, error } = useOwnershipNetwork(query);
  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return data?.groups ?? [];
    return (data?.groups ?? []).filter(
      (g) =>
        g.label.toLowerCase().includes(needle) ||
        g.backer?.toLowerCase().includes(needle) ||
        g.members.some((m) => m.name.toLowerCase().includes(needle)),
    );
  }, [data, search]);
  const selected = groups.find((g) => g.id === selectedId) ?? groups[0] ?? null;
  const activeCount =
    subs.size + ownership.size + (countries.size === 1 && countries.has('GB') ? 0 : countries.size);
  const fmt = (n: number) => formatEurInCurrency(n, ccy);

  function selectGroup(group: OwnershipGroupNode) {
    setSelectedId(group.id);
    setExpanded(new Set());
    setGroupFor(groupKey, { node: group, countries: [...countries] });
    setPickerOpen(false);
  }
  function resetFilters() {
    setCountries(new Set(['GB']));
    setSubs(new Set());
    setOwnership(new Set());
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div
        style={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
        }}
      >
        {/* 1 · Search row — input + filters, one line. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            borderBottom: '1px solid var(--hairline)',
          }}
        >
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <Search
              size={13}
              style={{ position: 'absolute', top: 8, left: 8, color: 'var(--text-tertiary)' }}
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPickerOpen(true);
              }}
              placeholder="Search owner, group or subsidiary"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '7px 8px 7px 27px',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface)',
                font: '400 var(--t-body) var(--font-ui)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <button onClick={() => setFiltersOpen(true)} style={button}>
            Filters{activeCount ? ` · ${activeCount}` : ''}
          </button>
          {activeCount > 0 && (
            <button onClick={resetFilters} style={button}>
              Clear
            </button>
          )}
        </div>

        {/* 2 · Group matches — a dropdown section under the search. The
            trigger shows the chosen group; expanding it lists every match. */}
        <div style={{ borderBottom: '1px solid var(--hairline)', flexShrink: 0 }}>
          <button
            type="button"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((o) => !o)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-primary)',
              textAlign: 'left',
              font: 'inherit',
              fontSize: 'var(--t-body)',
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: selected ? 600 : 400,
                color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)',
              }}
            >
              {isLoading
                ? 'Loading ownership groups…'
                : selected
                  ? `${selected.label} — ${selected.opcos} group entities · ${fmt(selected.revenueEur)}`
                  : 'Choose a group…'}
            </span>
            <span
              style={{ color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)', flexShrink: 0 }}
            >
              {groups.length} match{groups.length === 1 ? '' : 'es'}
            </span>
            <ChevronDown
              size={13}
              style={{
                color: 'var(--text-tertiary)',
                flexShrink: 0,
                transform: pickerOpen ? 'rotate(180deg)' : undefined,
              }}
            />
          </button>
          {pickerOpen && (
            <div
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                borderTop: '1px solid var(--hairline)',
              }}
            >
              <div
                style={{
                  padding: '7px 10px',
                  color: 'var(--text-tertiary)',
                  fontSize: 'var(--t-meta)',
                  lineHeight: 1.45,
                  borderBottom: '1px solid var(--hairline)',
                }}
              >
                Results are resolved economic groups. The hierarchy shows commercial structure, not
                an asserted legal ownership chain.
              </div>
              {error && <Empty label="Unable to load ownership groups." />}
              {!isLoading && !error && groups.length === 0 && (
                <Empty label="No groups match the current filters." />
              )}
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => selectGroup(g)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '9px 12px',
                    border: 'none',
                    borderBottom: '1px solid var(--hairline)',
                    background: selected?.id === g.id ? 'var(--hover-subtle)' : 'transparent',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 'var(--t-body)', marginBottom: 3 }}>
                    {g.label}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-meta)' }}>
                    {g.backer ?? 'No resolved ultimate owner'} ·{' '}
                    {OWNERSHIP_CLASS_LABEL[g.ownershipClass] ?? g.ownershipClass}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      color: 'var(--text-tertiary)',
                      fontSize: 'var(--t-meta)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {g.opcos} group entities · {g.countries.length} market
                    {g.countries.length === 1 ? '' : 's'} ·{' '}
                    {g.countries
                      .map((c) => MARKET_NAMES[c as keyof typeof MARKET_NAMES] ?? c)
                      .join(' · ')}{' '}
                    · {fmt(g.revenueEur)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 3 · The chosen group's entities (commercial view). */}
        <section
          style={{ minWidth: 0, minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          {selected ? (
            <Hierarchy
              group={selected}
              grouping={grouping}
              onGrouping={setGrouping}
              expanded={expanded}
              onToggle={(key) =>
                setExpanded((old) => {
                  const next = new Set(old);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                })
              }
              onEntity={(member) => {
                setEntity(member.id);
                setGroupFor(groupKey, { node: selected, countries: [...countries] });
              }}
              fmt={fmt}
            />
          ) : (
            <Empty label="Select a group to inspect its structure." />
          )}
        </section>
      </div>
      <Filters
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        countries={countries}
        setCountries={setCountries}
        subs={subs}
        setSubs={setSubs}
        ownership={ownership}
        setOwnership={setOwnership}
        onClear={resetFilters}
        resultCount={data?.summary.groupCount}
      />
    </PanelFrame>
  );
}

function Hierarchy({
  group,
  grouping,
  onGrouping,
  expanded,
  onToggle,
  onEntity,
  fmt,
}: {
  group: OwnershipGroupNode;
  grouping: Grouping;
  onGrouping: (v: Grouping) => void;
  expanded: Set<string>;
  onToggle: (k: string) => void;
  onEntity: (m: OwnershipMember) => void;
  fmt: (n: number) => string;
}) {
  const buckets = useMemo(() => {
    const out = new Map<string, OwnershipMember[]>();
    for (const m of group.members) {
      const key =
        grouping === 'country'
          ? `${m.country} · ${MARKET_NAMES[m.country as keyof typeof MARKET_NAMES] ?? m.country}`
          : grouping === 'holdco'
            ? (m.holdco ?? 'Directly held')
            : (m.subsegment ?? 'Other healthcare');
      out.set(key, [...(out.get(key) ?? []), m]);
    }
    return [...out.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [group, grouping]);
  return (
    <>
      <div
        style={{
          padding: '10px 13px',
          borderBottom: '1px solid var(--hairline)',
          background: 'var(--surface-elevated)',
        }}
      >
        <div
          style={{
            color: 'var(--text-tertiary)',
            fontSize: 'var(--t-meta)',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          Commercial view
        </div>
        <div style={{ fontSize: 'var(--t-panel-title)', fontWeight: 700, marginTop: 3 }}>
          {group.label}
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-meta)', marginTop: 3 }}>
          {group.backer ?? 'No resolved ultimate owner'} ·{' '}
          {OWNERSHIP_CLASS_LABEL[group.ownershipClass] ?? group.ownershipClass} ·{' '}
          {group.confidence ?? 'unconfirmed'} basis
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}
        >
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)' }}>
            Group branches by
          </span>
          {(['country', 'holdco', 'subsegment'] as Grouping[]).map((v) => (
            <button
              key={v}
              onClick={() => onGrouping(v)}
              style={{
                ...button,
                padding: '3px 6px',
                background: grouping === v ? 'var(--hover-subtle)' : 'var(--surface)',
              }}
            >
              {v === 'holdco' ? 'Legal shell' : v[0]!.toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '10px 13px' }}>
        <div
          style={{
            padding: '7px 9px',
            borderLeft: '2px solid var(--accent)',
            background: 'var(--hover-subtle)',
            fontSize: 'var(--t-body)',
          }}
        >
          <strong>{group.backer ?? group.label}</strong>
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-meta)', marginTop: 2 }}>
            {group.backer ? 'Resolved backer / ultimate owner' : 'Resolved group root'}
          </div>
        </div>
        <div style={{ marginLeft: 12, borderLeft: '1px solid var(--hairline)', paddingLeft: 11 }}>
          <div style={{ padding: '9px 0', fontSize: 'var(--t-body)', fontWeight: 650 }}>
            {group.label}
            <span
              style={{
                marginLeft: 7,
                color: 'var(--text-tertiary)',
                fontWeight: 400,
                fontSize: 'var(--t-meta)',
              }}
            >
              {group.opcos} entities · {fmt(group.revenueEur)}
            </span>
          </div>
          {buckets.map(([name, members]) => {
            const key = `${group.id}:${grouping}:${name}`;
            const open = expanded.has(key) || buckets.length <= 3;
            const shown = open ? members : members.slice(0, 0);
            return (
              <div
                key={key}
                style={{
                  marginLeft: 4,
                  borderLeft: '1px solid var(--hairline)',
                  paddingLeft: 11,
                  paddingBottom: 6,
                }}
              >
                <button
                  onClick={() => onToggle(key)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: '5px 0',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    font: '600 var(--t-body) var(--font-ui)',
                  }}
                >
                  {open ? '−' : '+'} {name}
                  <span
                    style={{
                      marginLeft: 6,
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      fontSize: 'var(--t-meta)',
                    }}
                  >
                    {members.length} entities
                  </span>
                </button>
                {shown.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onEntity(m)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      padding: '5px 0 5px 11px',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      font: '400 var(--t-meta) var(--font-ui)',
                    }}
                  >
                    {m.name}
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      {' '}
                      · {m.country}
                      {m.city ? ` · ${m.city}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        {group.members.length < group.opcos && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 9px',
              border: '1px solid var(--hairline)',
              color: 'var(--text-tertiary)',
              fontSize: 'var(--t-meta)',
            }}
          >
            This view shows the top {group.members.length} entities by revenue. The full-group count
            remains {group.opcos}.
          </div>
        )}
      </div>
    </>
  );
}

function Filters({
  open,
  onOpenChange,
  countries,
  setCountries,
  subs,
  setSubs,
  ownership,
  setOwnership,
  onClear,
  resultCount,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  countries: Set<string>;
  setCountries: (v: Set<string>) => void;
  subs: Set<string>;
  setSubs: (v: Set<string>) => void;
  ownership: Set<string>;
  setOwnership: (v: Set<string>) => void;
  onClear: () => void;
  resultCount?: number;
}) {
  const toggle = (
    current: Set<string>,
    set: (v: Set<string>) => void,
    value: string,
    keepOne = false,
  ) => {
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    if (keepOne && !next.size) next.add(value);
    set(next);
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,17,14,.35)', zIndex: 90 }}
        />
        <Dialog.Content
          style={{
            position: 'fixed',
            inset: '10% auto auto 50%',
            transform: 'translateX(-50%)',
            width: 620,
            maxWidth: '94vw',
            maxHeight: '78vh',
            overflowY: 'auto',
            zIndex: 91,
            border: '1px solid var(--panel-border)',
            borderRadius: 3,
            background: 'var(--surface)',
            padding: 16,
            boxShadow: '0 18px 42px rgba(0,0,0,.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <Dialog.Title style={{ margin: 0, fontSize: 'var(--t-panel-title)' }}>
              Ownership explorer filters
            </Dialog.Title>
            <Dialog.Close asChild>
              <button style={{ ...button, marginLeft: 'auto' }}>
                <X size={13} />
              </button>
            </Dialog.Close>
          </div>
          <FilterSet title="Country">
            {MARKETS.map((v) => (
              <Choice
                key={v}
                active={countries.has(v)}
                onClick={() => toggle(countries, setCountries, v, true)}
              >
                {MARKET_NAMES[v] ?? v}
              </Choice>
            ))}
          </FilterSet>
          <FilterSet title="Healthcare subsegment">
            {SUBSEGMENT_FILTER_OPTIONS.map((v) => (
              <Choice key={v} active={subs.has(v)} onClick={() => toggle(subs, setSubs, v)}>
                {SPECIALTY_TOKENS.has(v) ? (SPECIALTY_LABEL[v] ?? v) : v}
              </Choice>
            ))}
          </FilterSet>
          <FilterSet title="Ownership type">
            {OWNERSHIP_OPTIONS.map((v) => (
              <Choice
                key={v}
                active={ownership.has(v)}
                onClick={() => toggle(ownership, setOwnership, v)}
              >
                {OWNERSHIP_CLASS_LABEL[v]}
              </Choice>
            ))}
          </FilterSet>
          <div style={{ display: 'flex', gap: 7, marginTop: 14 }}>
            <button style={button} onClick={onClear}>
              Clear all
            </button>
            <Dialog.Close asChild>
              <button
                style={{
                  ...button,
                  marginLeft: 'auto',
                  background: 'var(--text-primary)',
                  color: 'var(--surface)',
                }}
              >
                Apply{resultCount != null ? ` · ${resultCount} groups` : ''}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
function FilterSet({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 13 }}>
      <div
        style={{
          color: 'var(--text-tertiary)',
          fontSize: 'var(--t-meta)',
          fontWeight: 700,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}
function Choice({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...button,
        padding: '4px 6px',
        background: active ? 'var(--hover-subtle)' : 'var(--surface)',
        borderColor: active ? 'var(--accent)' : 'var(--panel-border)',
      }}
    >
      {children}
    </button>
  );
}
function Empty({ label }: { label: string }) {
  return (
    <div style={{ padding: 18, color: 'var(--text-tertiary)', fontSize: 'var(--t-body)' }}>
      {label}
    </div>
  );
}
